<template>
  <page-list
    :list="list"
    :columns="columns"
    :single-actions="singleActions"
    :group-actions="groupActions"
    :export-data-options="exportDataOptions">
    <template v-slot:group-actions-append>
      <date-time
        v-if="showTime"
        ref="time"
        class="ml-2"
        :getParams.sync="dateParam"
        :dateMode.sync="dateMode"
        paramTimeFormatter="YYYY-MM-DDTZ"
        @refresh="fetchData" />
      <a-button type="primary" class="ml-2" @click="handleBillShare">{{$t('bill.immediate_share')}}</a-button>
    </template>
    <div class="mt-2 mb-1" slot="table-prepend">
      <total-price
      :label="$t('bill.cpp_total')"
      :secondaryLabel="$t('bill.total_postpaid')"
      :currencySign="currencySign"
      :total="total_amount"
      :secondary_total="total_postpaid_amount"
      :secondaryTotalShown="true" />
    </div>
  </page-list>
</template>

<script>
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import currencyRequest from '@Bill/mixins/currencyRequest'
import TotalPrice from '@Bill/sections/TotalPrice'
import storage from '@/utils/storage'
import {
  getServiceDescriptionFilter,
  getRegionFilter,
  getOsTypeFilter,
  getModelFilter,
} from '../../utils/filters'
import {
  getAccountingPeriodColumn,
  getServiceDescriptionColumn,
  getRegionColumn,
  getModelColumn,
  getAmountHoursColumn,
  getCppCountColumn,
  getUsedHoursColumn,
  getCppCostColumn,
  getOnDemandCostColumn,
  getOsTypeTableColumn,
} from '../../utils/columns'

const localTimeKey = '__oc_date_time_'

export default {
  name: 'BillCppSharedList',
  components: {
    TotalPrice,
  },
  mixins: [WindowsMixin, ListMixin, currencyRequest],
  props: {
    id: {
      type: String,
      default: 'CppSharedForBillAccountSidePage',
    },
    data: Object,
  },
  data () {
    return {
      list: this.$list.createList(this, {
        id: this.id,
        resource: 'month_cpp_reservations',
        apiVersion: 'v1',
        getParams: {},
        fetchDataCb: this.getTotal,
        filterOptions: {
          name: getServiceDescriptionFilter(),
          cpp_region: getRegionFilter(),
          os_type: getOsTypeFilter(),
          os_sku: getModelFilter(),
        },
      }),
      columns: [
        getAccountingPeriodColumn(),
        getServiceDescriptionColumn({}),
        getRegionColumn(),
        getModelColumn(),
        getOsTypeTableColumn(),
        getCppCountColumn(),
        getAmountHoursColumn(),
        getUsedHoursColumn(),
        getCppCostColumn(),
        getOnDemandCostColumn(),
      ],
      singleActions: [
        {
          label: this.$t('common_224'),
          permission: '',
          action: (obj) => {
            this.createDialog('CppSharedDetailDialog', {
              data: [obj],
            })
          },
        },
      ],
      exportDataOptions: {
        items: [
          { label: this.$t('bill.accounting_period'), key: 'month' },
          { label: this.$t('bill.service_description'), key: 'category' },
          { label: this.$t('bill.text_83'), key: 'cpp_region' },
          { label: this.$t('bill.model'), key: 'os_sku' },
          { label: this.$t('table.title.os'), key: 'os_type' },
          { label: this.$t('bill.count'), key: 'cpp_count' },
          { label: this.$t('bill.amount_hours'), key: 'cpp_total_hours' },
          { label: this.$t('bill.used_hours'), key: 'total_used_duration' },
          { label: this.$t('bill.cpp_cost'), key: 'cpp_cost' },
          { label: this.$t('bill.on_demand_cost'), key: 'postpaid_cost' },
        ],
        transformParams: (params) => {
          return { ...params, cloudaccount_id: this.data.id }
        },
      },
      dateParam: {},
      dateMode: 'month',
      total_amount: 0,
      total_postpaid_amount: 0,
      showTime: true,
    }
  },
  computed: {
  },
  watch: {
    dateParam: {
      handler () {
        this.fetchData()
      },
      immediate: true,
    },
  },
  created () {
  },
  methods: {
    initTime () {
      const localTime = storage.get(localTimeKey, {})
      const { dateMode, time = { } } = localTime
      if (!dateMode) return
      if (dateMode === 'custom' && time.start && time.end) {
        this.dateParam.start_date = this.$moment(time.start).format('YYYY-MM-DD') + 'TZ'
        this.dateParam.end_date = this.$moment(time.end).format('YYYY-MM-DD') + 'TZ'
        this.$refs.time.time.dateMode = dateMode
        this.$refs.time.time.date = [this.$moment(time.start), this.$moment(time.end)]
        this.$refs.customDate = { start: this.$moment(time.start), end: this.$moment(time.end) }
        this.$refs.time.handleDateModeChange()
      } else if (['month', 'last_month', 'quarter', 'last_quarter', 'year'].includes(dateMode)) {
        this.$refs.time.time.dateMode = dateMode
        this.$refs.time.customTimeLabel = ''
        let start = this.$moment()
        let end = this.$moment()
        if (dateMode === 'month') {
          start = start.startOf('month')
          end = end.endOf('month')
        } else if (dateMode === 'last_month') {
          start = start.subtract(1, 'month').startOf('month')
          end = end.subtract(1, 'month').endOf('month')
        } else if (dateMode === 'quarter') {
          start = start.startOf('quarter')
          end = end.endOf('quarter')
        } else if (dateMode === 'last_quarter') {
          start = start.subtract(1, 'quarter').startOf('quarter')
          end = end.subtract(1, 'quarter').endOf('quarter')
        } else if (dateMode === 'year') {
          start = start.startOf('year')
          end = end.endOf('year')
        }
        this.dateParam.start_date = start.format('YYYY-MM-DD') + 'TZ'
        this.dateParam.end_date = end.format('YYYY-MM-DD') + 'TZ'
        this.fetchData()
      }
    },
    getParams () {
      return {
        cloudaccount_id: this.data && this.data.id,
        details: true,
        start: this.dateParam.start_date || '',
        end: this.dateParam.end_date || '',
        order_by: 'month',
        order: 'desc',
      }
    },
    fetchData () {
      this.list.getParams = this.getParams()
      this.list.fetchData()
    },
    getTotal (response) {
      const { totals = {} } = response.data
      const { total_cpp_cost = 0, total_postpaid_cost = 0 } = totals
      this.total_amount = total_cpp_cost
      this.total_postpaid_amount = total_postpaid_cost
    },
    handleBillShare () {
      this.createDialog('BillSharingShareDialog', {
        account_id: this.data.id,
        task_type: 'prepaid_amortizing',
      })
    },
  },
}
</script>
